import { useCached3DObjectsIfReady } from "@/object-cache";
import { useAppSelector } from "@/store/store-hooks";
import {
  ImageCoreExtractor,
  isTextureImageData,
} from "@/utils/ImageCoreExtractor";
import { getSheetSize } from "@faro-lotv/app-component-toolbox";
import { IElementImgSheetTiled } from "@faro-lotv/ielement-types";
import { LodFloorPlan } from "@faro-lotv/lotv";
import { getElementTransformation } from "@faro-lotv/project-source";
import { useMemo } from "react";
import { Vector3Tuple } from "three";
import { getSheetCorner } from "./use-sheet-corners";

/**
 * @param sheets tiled sheets to compute the corners for
 * @returns the list of 4 corners of the "core" (area of interest, ignoring useless blank area) of a tile sheet
 */
export function useTiledSheetCore(
  sheets: IElementImgSheetTiled[],
): Vector3Tuple[] {
  const elementsState = useAppSelector((state) => state.iElements);

  const tiledSheets = useCached3DObjectsIfReady(sheets, "TiledSheetCore");

  return useMemo(
    () =>
      sheets.reduce((corners: Vector3Tuple[], sheetElement) => {
        const { worldMatrix } = getElementTransformation(
          elementsState,
          sheetElement.id,
        );
        // find the object for this element (might not be ready yet)
        const sheet = tiledSheets.find(
          (sheetObject) => sheetObject.iElement.id === sheetElement.id,
        );
        // the top level image (lowest resolution) is always preloaded as first texture by loadLodFloorPlan
        const image =
          sheet &&
          sheet instanceof LodFloorPlan &&
          isTextureImageData(sheet.textures[0].image)
            ? sheet.textures[0].image
            : undefined;
        if (image) {
          // get the size of the image in pixels
          const { width: imageWidth, height: imageHeight } =
            getSheetSize(sheetElement);
          corners.push(
            ...ImageCoreExtractor.findCoreFromImage(
              image,
              imageWidth,
              imageHeight,
              worldMatrix,
            ),
          );
        } else {
          // fall back to IElement's bounding box if the object is not ready yet, OR if this is  not a tiled image
          corners.push(...getSheetCorner(sheetElement, worldMatrix));
        }
        return corners;
      }, []),
    [elementsState, tiledSheets, sheets],
  );
}
